import { dateSuisse } from './dates';
import { today } from './utils';

/**
 * Les pièces de dépense de l'entreprise, chacune une seule fois, d'où
 * qu'elles viennent : la page Dépenses, les factures de fournisseurs saisies
 * sur un chantier, les factures jointes à une offre ou à une facture, et les
 * sous-traitants du chantier.
 *
 * <p>Le patron saisit une même facture à plusieurs endroits : jointe à
 * l'offre puis à la facture finale, saisie sur le chantier et dans Dépenses,
 * l'offre du sous-traitant jointe à l'offre du client et son prix noté au
 * chantier. Chaque écran dédoublonnait à sa façon, et seulement au même
 * montant le même jour : la même facture de Gétaz comptait deux fois, la
 * sous-traitance une fois au prix convenu et une fois à sa facture. Le
 * classeur, la comptabilité, l'accueil et le chantier lisent maintenant tous
 * cette liste-ci.
 *
 * <p>Deux pièces sont la même facture quand elles ont le même montant, au
 * centime, et :
 * <ul>
 *   <li>le même fournisseur (« Gétaz Romang SA » et « Getaz »), ou l'un des
 *       deux sans nom ;</li>
 *   <li>le même jour, ou le même numéro de facture du fournisseur ;</li>
 *   <li>pas deux chantiers ni deux affaires différents ;</li>
 *   <li>elles ne sont pas saisies au même endroit : deux factures de même
 *       montant tapées sur le même chantier sont deux factures.</li>
 * </ul>
 * Un sous-traitant compte pour ses factures ; son prix convenu ne compte que
 * pour ce qui n'est pas encore facturé.
 */

/** Les formes de société et petits mots qui ne disent pas qui est le fournisseur. */
const MOTS_VIDES = new Set([
  'sa',
  'sarl',
  'ag',
  'gmbh',
  'sas',
  'sagl',
  'ltd',
  'cie',
  'et',
  'de',
  'du',
  'des',
  'la',
  'le',
  'les',
  'and',
  'the',
  'succ',
]);

/** Les mots qui nomment un fournisseur, sans accents ni forme de société. */
export function motsDuNom(nom) {
  return String(nom || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(' — ')[0]
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((m) => m.length >= 3 && !MOTS_VIDES.has(m));
}

/**
 * Deux noms de fournisseur désignent-ils la même entreprise ?
 *
 * @param {string} a premier nom
 * @param {string} b second nom
 * @returns {boolean|null} null quand l'un des deux n'est pas noté
 */
export function memeFournisseur(a, b) {
  const ma = motsDuNom(a);
  const mb = motsDuNom(b);
  if (!ma.length || !mb.length) return null;
  return ma[0] === mb[0] || mb.includes(ma[0]) || ma.includes(mb[0]);
}

/** « 05.03.2026 » ou « 2026-03-05 » en nombre de jours, null sans date. */
function jours(d) {
  const s = String(d || '');
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]) / 864e5;
  m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (m) return Date.UTC(+m[3], +m[2] - 1, +m[1]) / 864e5;
  return null;
}

/** Les dépenses qui sont des achats : elles peuvent être la copie d'une facture de chantier. */
const ACHATS = new Set(['materiel', 'soustraitance', 'outillage', 'facture', 'marchandise']);

/** La sous-traitance se reconnaît à sa catégorie ; le reste est du matériel. */
export const typeDeLAchat = (categorie) =>
  /sous/i.test(String(categorie || '')) ? 'soustraitance' : 'materiel';

const centimes = (v) => Math.round((+v || 0) * 100);

/**
 * Les deux pièces sont-elles la même facture ?
 *
 * @param {object} a pièce déjà gardée
 * @param {object} b pièce à ranger
 * @returns {boolean} vrai si b est la copie de a
 */
export function memeFacture(a, b) {
  if (centimes(a.montant) !== centimes(b.montant) || centimes(a.montant) <= 0) return false;
  if (a.endroit === b.endroit) return false;
  if (
    a.chantierId != null &&
    b.chantierId != null &&
    String(a.chantierId) !== String(b.chantierId)
  ) {
    return false;
  }
  if (a.affaire && b.affaire && a.affaire !== b.affaire) return false;
  const noms = memeFournisseur(a.fournisseur, b.fournisseur);
  if (noms === false) return false;
  // Le même numéro de facture du fournisseur : c'est elle, quel que soit le jour noté.
  const ra = refDe(a);
  if (ra && ra === refDe(b)) return true;
  // Sinon, le même jour : le patron achète souvent chez le même fournisseur,
  // pour plusieurs chantiers, parfois le même montant ; deux achats à des
  // jours différents restent deux achats.
  const ja = jours(a.date);
  if (ja === null || ja !== jours(b.date)) return false;
  if (noms === true) return true;
  // Sans nom d'un côté, seul le jour les relie ; un café ne cache pas la
  // visserie du même montant le même jour.
  const memeAffaire =
    (a.chantierId != null && String(a.chantierId) === String(b.chantierId)) ||
    (!!a.affaire && a.affaire === b.affaire);
  const achat = (p) => p.source !== 'frais' || ACHATS.has(p.type);
  return memeAffaire || (achat(a) && achat(b));
}

/** Le numéro de la facture chez le fournisseur, sans espaces ni casse ; vide s'il n'est pas noté. */
const refDe = (p) =>
  String(p.item?.reference || p.item?.ref || '')
    .replace(/\s+/g, '')
    .toLowerCase();

/**
 * Toutes les pièces de dépense, chacune une fois.
 *
 * @param {object} p pièces de la société
 * @param {object[]} [p.frais] dépenses de la page Dépenses
 * @param {object[]} [p.chantiers] chantiers, leurs factures fournisseurs et sous-traitants
 * @param {object[]} [p.achatsJoints] pièces chiffrées des offres et factures
 *   ({genre, documentId, piece}, ou la pièce seule)
 * @param {object[]} [p.factures] factures clients : relient une pièce jointe à son chantier
 * @param {string|number} [p.chantierId] chantier des pièces jointes sans document (vue d'un chantier)
 * @returns {object[]} les pièces gardées : {source: 'frais'|'chantier'|'joint'|'soustraitant',
 *   type, montant, date, fournisseur, chantierId, affaire, endroit, item, doublons,
 *   facture?, sousTraitant?, joint?, chantier?}
 */
export function piecesDesDepenses({ frais, chantiers, achatsJoints, factures, chantierId }) {
  const listeCh = chantiers || [];
  const chParId = new Map(listeCh.map((ch) => [String(ch.id), ch]));
  const chParOffre = new Map(
    listeCh.filter((ch) => ch.devisId != null).map((ch) => [String(ch.devisId), ch]),
  );
  const affaireDuChantier = (ch) => (ch && ch.devisId != null ? 'D' + ch.devisId : null);
  const gardees = [];
  const range = (piece) => {
    const deja = gardees.find(
      (g) => !g.doublons.some((d) => d.endroit === piece.endroit) && memeFacture(g, piece),
    );
    if (deja) {
      deja.doublons.push(piece);
      return false;
    }
    gardees.push({ ...piece, doublons: [] });
    return true;
  };

  (frais || []).forEach((x, i) => {
    const ch =
      x.chantierId != null && x.chantierId !== '' ? chParId.get(String(x.chantierId)) : null;
    gardees.push({
      source: 'frais',
      type: x.type || 'autre',
      montant: +x.montant || 0,
      date: x.date,
      fournisseur: x.fournisseur || '',
      chantierId: x.chantierId != null && x.chantierId !== '' ? x.chantierId : null,
      affaire: affaireDuChantier(ch),
      endroit: 'frais:' + (x.id ?? 'i' + i),
      item: x,
      doublons: [],
    });
  });

  listeCh.forEach((ch) =>
    (ch.factures || []).forEach((f) =>
      range({
        source: 'chantier',
        type: typeDeLAchat(f.cat),
        montant: +f.montant || 0,
        date: f.date,
        fournisseur: f.fournisseur || '',
        chantierId: ch.id,
        affaire: affaireDuChantier(ch),
        endroit: 'chantier:' + ch.id,
        item: f,
        chantier: ch,
      }),
    ),
  );

  (achatsJoints || []).forEach((x, i) => {
    const p = x?.piece || x;
    const montant = +p?.montant || 0;
    if (montant <= 0) return;
    const genre = x?.genre;
    const docId = x?.documentId;
    let ch = null;
    let affaire = null;
    if (genre === 'DEVIS' && docId != null) {
      affaire = 'D' + docId;
      ch = chParOffre.get(String(docId)) || null;
    } else if (genre === 'FACTURE' && docId != null) {
      const fac = (factures || []).find((f) => f.id === docId);
      affaire = fac?.devisId != null ? 'D' + fac.devisId : 'F' + docId;
      ch =
        (fac?.siteId != null && chParId.get(String(fac.siteId))) ||
        (fac?.devisId != null && chParOffre.get(String(fac.devisId))) ||
        null;
    }
    if (!ch && chantierId != null) ch = chParId.get(String(chantierId)) || null;
    const idCh = ch ? ch.id : (chantierId ?? null);
    range({
      source: 'joint',
      type: typeDeLAchat(p.categorie),
      montant,
      date: dateSuisse(p.date),
      fournisseur: p.fournisseur || p.libelle || '',
      chantierId: idCh,
      affaire: affaire || affaireDuChantier(ch),
      endroit: genre && docId != null ? 'doc:' + genre + docId : 'joint:' + (p.id ?? 'i' + i),
      item: p,
      joint: x,
    });
  });

  // Un sous-traitant compte pour ses factures, où qu'elles soient saisies ;
  // son prix convenu, pour ce qui n'est pas encore facturé.
  listeCh.forEach((ch) => {
    const affaire = affaireDuChantier(ch);
    const prises = new Set();
    (ch.sousTraitants || [])
      .filter((t) => +t.prixConvenu > 0)
      .forEach((t) => {
        const prix = +t.prixConvenu || 0;
        const duChantier = (g) =>
          (g.chantierId != null && String(g.chantierId) === String(ch.id)) ||
          (!!affaire && g.affaire === affaire);
        let factureLui = gardees.filter(
          (g) =>
            g.source !== 'soustraitant' &&
            !prises.has(g) &&
            duChantier(g) &&
            memeFournisseur(g.fournisseur, t.entreprise) === true,
        );
        // Sans nom reconnu : une facture du chantier de son montant exact est la sienne.
        if (!factureLui.length) {
          const exacte = gardees.find(
            (g) =>
              g.source === 'chantier' &&
              !prises.has(g) &&
              String(g.chantierId) === String(ch.id) &&
              centimes(g.montant) === centimes(prix),
          );
          if (exacte) factureLui = [exacte];
        }
        factureLui.forEach((g) => prises.add(g));
        const facture = factureLui.reduce((s, g) => s + (+g.montant || 0), 0);
        const reste = Math.round((prix - facture) * 100) / 100;
        if (reste <= 0) return;
        const date = ch.debut ? dateSuisse(ch.debut) : today();
        gardees.push({
          source: 'soustraitant',
          type: 'soustraitance',
          montant: reste,
          date,
          fournisseur: t.entreprise || '',
          chantierId: ch.id,
          affaire,
          endroit: 'soustraitant:' + ch.id,
          item: t,
          sousTraitant: t,
          chantier: ch,
          // La TVA de sa facture, au prorata de ce qui reste à facturer.
          montantTva:
            t.montantTva != null && prix > 0
              ? Math.round(((+t.montantTva * reste) / prix) * 100) / 100
              : null,
          dejaFacture: facture,
          // Le reste du prix convenu, une fois ses premières factures reçues :
          // un coût du chantier, mais pas encore de TVA à récupérer — l'AFC
          // ne l'admet que sur la facture. Noté au montant de sa facture, sans
          // autre facture, le sous-traitant garde la sienne.
          sansFacture: facture > 0,
          prixConvenu: prix,
          doublons: [],
        });
      });
  });

  return gardees;
}
